import type { ReactNode } from "react";
import { BluebirdLayout } from "@/components/bluebird/BluebirdLayout";
import { BreadcrumbJsonLd } from "@/components/shared/json-ld";
import { SlimFooter, SubHero, SubMasthead } from "@/components/bluebird/Chrome";
import { Button, H2 } from "@/components/bluebird/primitives";
import { BluebirdContactForm } from "@/components/bluebird/ContactForm";
import { color, maxWidth, radius, space, type } from "@/lib/design-tokens";
import { contact as copy } from "@/lib/copy/contact";
import { shop } from "@/lib/copy/shop";

interface Props {
  /** `?about=` — set by CTAs elsewhere so the topic arrives pre-selected. */
  about?: string;
  /** `?newsletter_email=` (plus direct `?email=`) preserves the newsletter handoff. */
  email?: string;
}

/**
 * `/contact` lost its nav slot, not its existence. It is the POST target for
 * every form on the site and its URL must not move.
 */
function Contact({ about, email }: Props) {
  return (
    <>
      {/* Structured data only — renders no visible markup, so it cannot move a
          pixel in script/design_diff.mjs. Kept from the previous design because
          breadcrumbs are what let a search result show the path to the page. */}
      <BreadcrumbJsonLd items={[{ name: "Contact", href: "/contact" }]} />

      <SubMasthead
        current="/contact"
        photo="hero-contact"
        photoAlt="A child looking into the box of a cargo bike"
      >
        <SubHero
          line1={copy.hero.line1}
          line2={copy.hero.line2}
          intro={copy.hero.intro}
          introMaxWidth="620px"
        />
      </SubMasthead>

      {/* The <main> landmark. A screen-reader user's "skip to content" jump
          lands here, and without it the only landmarks on the page are a banner
          and a footer with everything else unlabelled. <main> is display:block
          with no margin, so it wraps the same boxes at the same positions —
          verified by script/design_diff.mjs, which still reports 0%. */}
      <main>

      <section
        data-section="form"
        style={{
          maxWidth,
          margin: "0 auto",
          padding: `${space.sectionY} ${space.gutter}`,
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(300px,100%),1fr))",
          gap: "clamp(32px,5vw,72px)",
          alignItems: "start",
        }}
      >
        <BluebirdContactForm presetTopic={about} initialEmail={email} />

        <div
          style={{
            background: color.surface,
            borderRadius: radius.panel,
            padding: "clamp(24px,3vw,36px)",
          }}
        >
          {/* `type.h3` — the comp's card-heading step. This was a hand-written
              clamp(22px,2.6vw,28px), resolving to 28px/22px, neither of which
              is on the comp's type scale. Found by token_conformance.mjs. */}
          <H2 style={{ fontSize: type.h3 }}>{copy.asideHeading}</H2>
          <p style={{ margin: "12px 0 0", fontSize: "16px", lineHeight: 1.55, color: color.inkMuted }}>
            {copy.asideBody}
          </p>
          <div style={{ margin: "20px 0 0", fontSize: "16px", lineHeight: 1.8 }}>
            <div>
              <a href={shop.phoneHref} className="bb-underline-grow" style={{ fontWeight: 700 }}>
                {shop.phone}
              </a>
            </div>
            <div>
              <a href={shop.emailHref} className="bb-underline-grow">
                {shop.email}
              </a>
            </div>
            <div style={{ marginTop: "12px", fontWeight: 700 }}>{shop.hoursOpen}</div>
            <div style={{ color: color.red, fontWeight: 500 }}>{shop.hoursClosed}</div>
            <div style={{ marginTop: "10px" }}>
              <a
                href={shop.mapHref}
                target="_blank"
                rel="noopener noreferrer"
                className="bb-underline-grow"
                style={{ color: "inherit", display: "inline-block" }}
              >
                {shop.addressLine1}
                <br />
                {shop.addressLine2}
              </a>
            </div>
          </div>
          <div style={{ marginTop: "22px" }}>
            <Button
              href="/about#visit"
              variant="blue"
              style={{ fontSize: "15px", padding: "14px 22px" }}
            >
              Hours &amp; directions
            </Button>
          </div>
        </div>
      </section>

      </main>

      <SlimFooter borderTop />
    </>
  );
}

Contact.layout = (page: ReactNode) => <BluebirdLayout>{page}</BluebirdLayout>;

export default Contact;
